import { camelCase, capitalize } from 'lodash';
import { defineStore } from 'pinia';

import { fetchUserSubscription, getPlanDetail } from '@/api/apiService';
import { usePlan } from '@/composables/shared/usePlan';
import { useUserStore } from '@/stores/user';
import { Nullable } from '@/types/common';
import {
  CurrencyType,
  PaymentType,
  PlanDetails,
  PlanPricing,
  PlanStatusType,
  TypePlan,
  UserPlanType,
} from '@/types/components';
import { SUBSCRIPTION_BENEFITS } from '@/utils/constants/subscription';

export const useSubscriptionStore = defineStore('subscription', () => {
  const userStore = useUserStore();

  const { formatBenefitTranslation } = usePlan();

  const isSubscriptionLoading = ref<boolean>(false);

  const currentCredits = ref<number>(0);
  const purchasedCredits = ref<number>(0);

  const currentPlan = ref<TypePlan>('FREE');
  const currentPlanStatus = ref<PlanStatusType>('NORMAL');

  const currentPlanDetails = ref<PlanDetails>();
  const currentPlanBenefits = ref<string[]>([]);
  const plannedPlanDetails = ref<PlanDetails>();

  const currency = ref<string>('KRW');
  const paymentType = ref<PaymentType>('NONE');
  const lastPaymentErrorCode = ref<Nullable<string>>(null);
  const isRechargeLimitFull = ref<boolean>(false);
  const usagePercentage = ref<number>(0);

  const planPrices = ref<Record<string, PlanPricing>>({});

  const userPlan = computed<UserPlanType>(() => capitalize(camelCase(currentPlan.value)) as UserPlanType);

  const initializeSubscriptionInfo = async () => {
    await fetchAllPlanPricing();
    await fetchSubscriptionInfo();
  };

  const fetchSubscriptionInfo = async () => {
    isSubscriptionLoading.value = true;

    try {
      const { data } = await fetchUserSubscription();
      if (!data.subscription || data.subscription.length === 0) {
        throw Error;
      }

      currentCredits.value = data.creditPurchased - data.creditUsed || 0;
      purchasedCredits.value = data.creditPurchased;

      lastPaymentErrorCode.value = data.lastPaymentErrorCode;
      usagePercentage.value = data.usagePercentage;
      isRechargeLimitFull.value = data.isRechargeLimitFull;

      const userPaymentType = data.subscription.length > 0 ? data.subscription[0].paymentType : 'NONE';
      paymentType.value = userPaymentType;
      currency.value = setUserCurrency(userPaymentType);

      const activePlan = data.subscription.find((sub) => sub.status === 'NORMAL');
      currentPlan.value = activePlan.plan;
      currentPlanDetails.value = activePlan;
      currentPlanBenefits.value = SUBSCRIPTION_BENEFITS[activePlan.plan].map((benefit) =>
        formatBenefitTranslation(benefit, planPrices.value[activePlan.plan])
      );

      currentPlanStatus.value = getPlanStatus(data.subscription);

      plannedPlanDetails.value = data.subscription.find((sub) => sub.status === 'PLANNED');
    } catch (error) {
      console.error('사용자 구독 정보 조회 실패: ', error);
    } finally {
      isSubscriptionLoading.value = false;
    }
  };

  const fetchAllPlanPricing = async () => {
    try {
      const { success, data } = await getPlanDetail();

      if (success) {
        data.forEach(({ plan, planDetail }) => (planPrices.value[plan] = planDetail));
      }
    } catch (error) {
      console.error('플랜별 가격 조회 실패: ', error);
    }
  };

  const getPlanStatus = (subscriptionList: PlanDetails[]): PlanStatusType => {
    if (subscriptionList.some((sub) => sub.status === 'PLANNED')) {
      return 'PLANNED';
    }

    const activePlan = subscriptionList.find((sub) => sub.status === 'NORMAL');

    return activePlan?.endDate ? 'CANCEL' : 'NORMAL';
  };

  const setUserCurrency = (paymentType: PaymentType): CurrencyType => {
    if (paymentType && paymentType !== 'NONE') {
      return paymentType === 'TOSS' ? 'KRW' : 'USD';
    }

    const isKorean = userStore.userInfo.countryCode === 'KR';
    return isKorean ? 'KRW' : 'USD';
  };

  return {
    //state
    isSubscriptionLoading,
    currentCredits,
    purchasedCredits,
    currentPlan,
    currentPlanDetails,
    plannedPlanDetails,
    currentPlanBenefits,
    currentPlanStatus,
    currency,
    paymentType,
    planPrices,
    lastPaymentErrorCode,
    isRechargeLimitFull,
    usagePercentage,
    //getter
    userPlan,
    //method
    initializeSubscriptionInfo,
    fetchSubscriptionInfo,
    fetchAllPlanPricing,
  };
});
